import { useState, useCallback } from 'react';
import type { NotificationProps } from '@/components/ui/Notification';

export interface NotificationItem extends NotificationProps {
  id: string;
}

export const useNotification = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const addNotification = useCallback((notification: Omit<NotificationProps, 'onClose'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const newNotification: NotificationItem = {
      ...notification,
      id,
    };

    setNotifications((prev) => [...prev, newNotification]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const success = useCallback(
    (message: React.ReactNode, description?: React.ReactNode, duration?: number) => {
      return addNotification({
        type: 'success',
        message,
        description,
        duration,
      });
    },
    [addNotification]
  );

  const error = useCallback(
    (message: React.ReactNode, description?: React.ReactNode, duration?: number) => {
      return addNotification({
        type: 'error',
        message,
        description,
        duration,
      });
    },
    [addNotification]
  );

  const warning = useCallback(
    (message: React.ReactNode, description?: React.ReactNode, duration?: number) => {
      return addNotification({
        type: 'warning',
        message,
        description,
        duration,
      });
    },
    [addNotification]
  );

  const info = useCallback(
    (message: React.ReactNode, description?: React.ReactNode, duration?: number) => {
      return addNotification({
        type: 'info',
        message,
        description,
        duration,
      });
    },
    [addNotification]
  );

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    warning,
    info,
  };
};

